import type { GetCardOptions } from '../models/Cards/GetCardOptions';
import { EObject } from '../models/EObject';
import { doBufferedRequest } from '../store/requests/buffered-requests.service';
import type { OneRequestParams } from '@sydle/ui-api';
import type { FetchResponse, RestService } from '@sydle/ui-api';
import { buildRequestParams } from '@sydle/ui-api';

// The maximum URL Lenght accepted by modern browsers.
// Reference: Microsoft Edge
export const MAX_URL_LENGHT = 2048;

export type GetImageCardUrlParams = {
  _classId: string;
  _id: string;
  classFileId: string;
  objectFileId: string;
  fileId: string;
};
export class WorkspaceAPI {
  private restService: RestService;

  constructor(restService: RestService) {
    this.restService = restService;
  }

  getCards(options: GetCardOptions, buffered = true): Promise<FetchResponse<any>> {
    // Valida se são todas as referências da mesma classe
    const classId = options.objectsReferences[0]._classId;
    const isSameClassId = options.objectsReferences.every((el) => el._classId == classId);

    const body = {
      ...options,
      classId: isSameClassId ? classId : EObject.CLASS_ID,
    };

    const mergeArgs = (args, newArgs) => {
      const newObjectsReferences = newArgs.body.objectsReferences;
      const objectsReferences = [...(args?.body.objectsReferences ?? [])];

      newObjectsReferences.forEach((ref) => {
        if (!objectsReferences.find((el) => el._id == ref._id)) {
          objectsReferences.push(ref);
        }
      });

      // Valida se são todas as referências da mesma classe
      const classId = objectsReferences[0]._classId;
      const isSameClassId = objectsReferences.every((el) => el._classId == classId);

      const mergedArgs = {
        ...args,
        ...newArgs,
        body: {
          ...args.body,
          ...newArgs.body,

          // FIXME: This filter is necessary until we fix userPreferences. BUG005115
          objectsReferences: objectsReferences.filter((ref) => ref?._classId && ref?._id),
        },
        classId: isSameClassId ? classId : EObject.CLASS_ID,
      };

      return mergedArgs;
    };

    const splitResponse = (args, response) => {
      const {
        body: { objectsReferences },
      } = args;

      if (response.error) {
        return { error: response.error };
      }

      const cards = response.data?.cards?.filter((c) =>
        objectsReferences.find((ref) => ref._classId === c.classId && c._id === ref._id),
      );

      return { data: { cards, total: cards?.length } };
    };

    const args = {
      packageIdentifier: '_system',
      classIdentifier: '_workspace',
      methodIdentifier: 'getCards',
      body,
    };

    const requestFn = (requestArgs: OneRequestParams) => {
      const requestBodyString = this.encodeBodyToString(requestArgs.body);

      if (requestBodyString.length < MAX_URL_LENGHT) {
        const requestParamsAsQuery = this.convertBodyArgsToQueryParams(requestArgs, requestBodyString);
        const requestParams = buildRequestParams(requestParamsAsQuery);
        return this.restService.get(requestParams);
      }

      const requestParams = buildRequestParams(requestArgs);
      return this.restService.post(requestParams);
    };

    if (buffered) {
      return doBufferedRequest(`getCards` + `/${options.storage}`, args, requestFn, {
        mergeArgs,
        debounceTime: 50,
        splitResponse,
      });
    }

    return requestFn(args);
  }

  private encodeBodyToString(body) {
    return encodeURIComponent(JSON.stringify(body));
  }

  private convertBodyArgsToQueryParams(requestArgs: OneRequestParams, encodedBody: string): OneRequestParams {
    if (!requestArgs.body) return requestArgs;

    const newRequestArgs = JSON.parse(JSON.stringify(requestArgs));
    delete newRequestArgs.body;
    newRequestArgs.queryParams = { _body: null };
    newRequestArgs.queryParams._body = encodedBody;
    return newRequestArgs;
  }

  getConcreteClassesCards(options: { _id: string; includeInterfaceCard: boolean }): Promise<FetchResponse<any>> {
    const body = {
      ...options,
    };

    const requestParams = buildRequestParams({
      packageIdentifier: '_system',
      classIdentifier: '_workspace',
      methodIdentifier: 'getConcreteClassesCards',
      queryParams: {
        _body: encodeURIComponent(JSON.stringify(body)),
      },
    });

    return this.restService.get(requestParams);
  }

  getImageCardUrl(args: GetImageCardUrlParams): string {
    const { _classId, _id, classFileId, objectFileId, fileId } = args;
    return (
      `_system/_workspace/getImageCard` +
      `?classId=${_classId}&objectId=${_id}&classFileId=${classFileId}&objectFileId=${objectFileId}&fileId=${fileId}`
    );
  }

  getNotificationUrl(): string {
    return `${this.restService.config.baseUrl + this.restService.config.app}/_notifications`;
  }
}
